import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Globe, Plus } from "lucide-react";
import { useTran } from "@/hooks";
import { PageHeader } from "@/components/common";
import { Can } from "@/hooks/useCan/Can";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/hooks";
import { checkUserFromRole } from "@/helpers";
import { useOrganizationStore } from "@/features/organization";
import ReportingBoundaryTable from "../components/ReportingBoundaryTable";
import { useNavigate } from "react-router-dom";
import {
  loadFrameworks,
  loadReportingYears,
  loadReportingYearsList,
} from "../operations/reporting-boundary.ops";
import { reportingYearStore } from "../stores/reporting-year.store";
import { useCallback } from "react";

const ReportingBoundaryTableSkeleton = () => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
      <Skeleton className="h-9 w-full max-w-xs rounded-md" />
      <div className="flex items-center gap-2">
        <Skeleton className="h-9 w-[140px] rounded-md" />
        <Skeleton className="h-9 w-9 rounded-md" />
      </div>
    </div>

    <div className="overflow-x-auto rounded-md border border-border">
      <div className="grid min-w-[900px] grid-cols-[1fr_1.4fr_1.2fr_1.5fr_0.8fr_1.2fr_0.5fr] gap-4 border-b border-border px-4 py-3">
        {Array.from({ length: 7 }).map((_, index) => (
          <Skeleton
            key={`reporting-boundary-skeleton-head-${index}`}
            className="h-4 w-full"
          />
        ))}
      </div>

      {Array.from({ length: 5 }).map((_, rowIndex) => (
        <div
          key={`reporting-boundary-skeleton-row-${rowIndex}`}
          className="grid min-w-[900px] grid-cols-[1fr_1.4fr_1.2fr_1.5fr_0.8fr_1.2fr_0.5fr] gap-4 border-b border-border px-4 py-4 last:border-b-0"
        >
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-32" />
          <div className="flex gap-2">
            <Skeleton className="h-6 w-10 rounded-full" />
            <Skeleton className="h-6 w-10 rounded-full" />
            <Skeleton className="h-6 w-10 rounded-full" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-6 w-16 rounded-full" />
          </div>
          <Skeleton className="h-6 w-12 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-2 w-full rounded-full" />
            <Skeleton className="h-2 w-full rounded-full" />
          </div>
          <Skeleton className="h-8 w-8 rounded-md" />
        </div>
      ))}
    </div>
  </div>
);

const ReportingBoundaryPage = observer(() => {
  const { t } = useTran();
  const { user, userRole } = useAuthStore();
  const permissions = user?.permissions || [];
  const navigate = useNavigate();
  const isUserLevel = checkUserFromRole(userRole);

  const getCurrentCompanyId = (user) =>
    user?.companyId || user?.company?._id || user?.company?.id || user?.orgId;

  const companyId = getCurrentCompanyId(user);
  const [reportingYears, setReportingYears] = useState([]);
  const { availableFrameworks } = reportingYearStore;
  const [reportingYearsLoading, setReportingYearsLoading] = useState(true);

  const fetchReportingYears = useCallback(async () => {
    setReportingYearsLoading(true);
    try {
      const data = await loadReportingYearsList();
      setReportingYears(data);
    } finally {
      setReportingYearsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReportingYears();

    loadFrameworks();

    return () => {
      reportingYearStore.reset();
    };
  }, [fetchReportingYears]);

  const {
    fetchHierarchy,
    tree: organizationTree,
    loading: organizationLoading,
  } = useOrganizationStore();

  useEffect(() => {
    if (organizationTree.length > 0 || organizationLoading) return;
    fetchHierarchy().catch(() => {});
  }, [fetchHierarchy, organizationLoading, organizationTree.length]);

  const normalizeOrganizationTree = (nodes = []) => {
    const result = [];

    const walk = (items) => {
      items.forEach((org) => {
        result.push({
          id: org.id || org._id || org.key,
          code: org.code,
          name: org.name,
          parentId: org.parentId || org.parent_id,
        });

        if (org.children?.length) {
          walk(org.children);
        }
      });
    };

    walk(nodes);

    return result;
  };

  const allOrganizations = normalizeOrganizationTree(organizationTree);

  const company = allOrganizations.find(
    (org) => String(org.id) === String(companyId),
  );

  const normalizedReportingYears = reportingYears.map((item) => {
    let organizations;

    if (item.isAllOrganization) {
      organizations = [...allOrganizations];
    } else {
      organizations = (item.organizations ?? [])
        .map((organization) => {
          const orgId =
            organization?.id ||
            organization?._id ||
            organization?.value ||
            organization;

          return allOrganizations.find(
            (org) => String(org.id) === String(orgId),
          );
        })
        .map((organization, index) => {
          if (organization) return organization;

          const sourceOrganization = item.organizations?.[index];
          if (!sourceOrganization || typeof sourceOrganization !== "object") {
            return null;
          }

          return {
            id: sourceOrganization.id || sourceOrganization._id,
            code: sourceOrganization.code,
            name: sourceOrganization.name || sourceOrganization.label,
            parentId: sourceOrganization.parentId,
          };
        })
        .filter(Boolean);

      if (
        item.isCompanySelected &&
        company &&
        !organizations.some((org) => String(org.id) === String(company.id))
      ) {
        organizations.unshift(company);
      }
    }

    return {
      ...item,
      organizations,
    };
  });
  //console.log("Reporting Years:", normalizedReportingYears);

  const pageLoading = reportingYearsLoading || organizationLoading;

  // for demo only
  // if (user?.companyName !== "Company Alpha") {
  //   return <ReportingBoundaryPageOld />;
  // }

  return (
    <div className="flex-1 flex flex-col min-h-0 gap-y-6">
      {/* Header */}
      <PageHeader
        title={t("navigation.reportingBoundary")}
        description={t("reportingBoundary.pageDescription")}
        icon={Globe}
        actions={
          <Can
            resource="reporting_boundary"
            action="create"
            userPermissions={permissions}
          >
            <Button className="gap-2" onClick={() => navigate("add")}>
              <Plus className="h-4 w-4" />
              {t("Add Reporting Year")}
            </Button>
          </Can>
        }
      />

      <Can
        resource="reporting_boundary"
        action="view"
        userPermissions={permissions}
        showMessage={true}
      >
        {/* Main Layout */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Left Content */}
          <div className="xl:col-span-4 flex flex-col gap-6">
            {/* Table */}
            {/* {pageLoading ? (
              <ReportingBoundaryTableSkeleton />
            ) : ( */}
            <ReportingBoundaryTable
              data={normalizedReportingYears}
              organizationTree={organizationTree}
              onDeleted={fetchReportingYears}
              availableFrameworks={availableFrameworks}
              permissions={permissions}
              isUserLevel={isUserLevel}
              loading={pageLoading}
            />
            {/* )} */}
          </div>

          {/* Right Sidebar */}
          {/* <div className="flex flex-col gap-6">
          <SummaryPanel stats={stats} />
          <QuickActions />
        </div> */}
        </div>
      </Can>
    </div>
  );
});

export default ReportingBoundaryPage;
